import React from "react";
import {
  Datagrid,
  DateField,
  FunctionField,
  List,
  TextField,
  TopToolbar,
  useDataProvider,
  useListContext,
  useNotify,
  useRecordContext,
  useRefresh,
} from "react-admin";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField as FormTextField,
  TextField as MuiTextField,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import type { ThirdPartyClient } from "../../models/ThirdPartyClient";

type ResetSecretResponse = ThirdPartyClient;

type ThirdPartyClientFormValues = {
  name: string;
  scope: string;
  request_template_id: string;
};

const parseScopeTokens = (value?: string | null): string[] => {
  const rawTokens = (value ?? "")
    .split(/\s+/)
    .map((item) => item.trim())
    .filter(Boolean);
  return Array.from(new Set(rawTokens));
};

const stringifyScopeTokens = (tokens: string[]): string =>
  Array.from(new Set(tokens)).join(" ");

const ScopeChips: React.FC<{
  value?: string | null;
  removable?: boolean;
  onDelete?: (scope: string) => void;
}> = ({ value, removable = false, onDelete }) => {
  const scopes = parseScopeTokens(value);

  if (scopes.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        -
      </Typography>
    );
  }

  return (
    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
      {scopes.map((scope) => {
        const canDelete = removable;
        return (
          <Chip
            key={scope}
            label={scope}
            size="small"
            variant="outlined"
            onDelete={canDelete ? () => onDelete?.(scope) : undefined}
            sx={{
              borderRadius: "999px",
              fontWeight: 500,
            }}
          />
        );
      })}
    </Stack>
  );
};

const createDefaultFormValues = (
  record?: Partial<ThirdPartyClient> | null
): ThirdPartyClientFormValues => ({
  name: record?.name ?? "",
  scope: stringifyScopeTokens(parseScopeTokens(record?.scope)),
  request_template_id: record?.request_template_id ?? "",
});

const ThirdPartyClientFormDialog: React.FC<{
  open: boolean;
  mode: "create" | "edit";
  record?: ThirdPartyClient | null;
  onClose: () => void;
}> = ({ open, mode, record, onClose }) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const [submitting, setSubmitting] = React.useState(false);
  const [formValues, setFormValues] = React.useState<ThirdPartyClientFormValues>(
    createDefaultFormValues(record)
  );

  React.useEffect(() => {
    if (!open) return;
    setFormValues(createDefaultFormValues(record));
  }, [open, record]);

  const updateField = (key: keyof ThirdPartyClientFormValues, value: string) => {
    setFormValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleDeleteScope = (scope: string) => {
    setFormValues((prev) => ({
      ...prev,
      scope: stringifyScopeTokens(
        parseScopeTokens(prev.scope).filter((item) => item !== scope)
      ),
    }));
  };

  const handleSubmit = async () => {
    const payload = {
      name: formValues.name.trim(),
      scope: stringifyScopeTokens(parseScopeTokens(formValues.scope)),
      request_template_id: formValues.request_template_id.trim() || null,
    };

    if (!payload.name) {
      notify("请输入客户端名称", { type: "warning" });
      return;
    }
    if (!payload.scope) {
      notify("请输入 Scope", { type: "warning" });
      return;
    }

    setSubmitting(true);
    try {
      if (mode === "edit" && record) {
        await dataProvider.update("thirdPartyClients", {
          id: record.id,
          data: payload,
          previousData: record,
        });
        notify("客户端信息已更新", { type: "success" });
      } else {
        await dataProvider.create("thirdPartyClients", {
          data: payload,
        });
        notify("客户端创建成功", { type: "success" });
      }
      refresh();
      onClose();
    } catch {
      notify(mode === "edit" ? "更新失败" : "创建失败", { type: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{mode === "edit" ? "编辑第三方客户端" : "新建第三方客户端"}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <FormTextField
            label="客户端名称"
            value={formValues.name}
            onChange={(e) => updateField("name", e.target.value)}
            fullWidth
            size="small"
            required
          />
          <FormTextField
            label="作用域(Scope)"
            value={formValues.scope}
            onChange={(e) => updateField("scope", e.target.value)}
            fullWidth
            size="small"
            required
            helperText="支持输入多个 scope，使用空格分隔。"
          />
          <Box
            sx={{
              px: 1.5,
              py: 1.5,
              borderRadius: 2,
              border: "1px dashed rgba(148, 163, 184, 0.4)",
              backgroundColor: "rgba(15, 23, 42, 0.02)",
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              Scope 预览
            </Typography>
            <ScopeChips
              value={formValues.scope}
              removable
              onDelete={handleDeleteScope}
            />
          </Box>
          <FormTextField
            label="短信模板ID"
            value={formValues.request_template_id}
            onChange={(e) => updateField("request_template_id", e.target.value)}
            fullWidth
            size="small"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          取消
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          {submitting ? "提交中..." : mode === "edit" ? "保存" : "确认新建"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ThirdPartyClientListActions: React.FC = () => {
  const { filterValues, setFilters } = useListContext();
  const [name, setName] = React.useState<string>(String(filterValues?.name ?? ""));
  const [createOpen, setCreateOpen] = React.useState(false);

  React.useEffect(() => {
    setName(String(filterValues?.name ?? ""));
  }, [filterValues?.name]);

  const applyFilter = () => {
    const trimmed = name.trim();
    const nextFilters: Record<string, unknown> = { ...(filterValues ?? {}) };
    if (trimmed) {
      nextFilters.name = trimmed;
    } else {
      delete nextFilters.name;
    }
    setFilters(nextFilters, undefined, false);
  };

  const resetFilter = () => {
    setName("");
    const nextFilters: Record<string, unknown> = { ...(filterValues ?? {}) };
    delete nextFilters.name;
    setFilters(nextFilters, undefined, false);
  };

  return (
    <>
      <TopToolbar
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          px: 0,
          py: 1,
          minHeight: "auto",
        }}
      >
        <Button
          variant="contained"
          onClick={() => setCreateOpen(true)}
          sx={{
            borderRadius: 2,
            px: 2,
            py: 1,
            fontWeight: 600,
            boxShadow: "0 8px 24px rgba(25, 118, 210, 0.24)",
          }}
        >
          新建客户端
        </Button>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ ml: "auto", flexWrap: "wrap" }}
        >
          <MuiTextField
            size="small"
            value={name}
            label="客户端名称"
            placeholder="客户端名称"
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                applyFilter();
              }
            }}
            sx={{
              width: 280,
              minWidth: 220,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                backgroundColor: "background.paper",
              },
            }}
          />
          <Button
            variant="outlined"
            onClick={resetFilter}
            sx={{ borderRadius: 2, px: 2, whiteSpace: "nowrap" }}
          >
            重置
          </Button>
          <Button
            variant="contained"
            onClick={applyFilter}
            sx={{ borderRadius: 2, px: 2, whiteSpace: "nowrap" }}
          >
            查询
          </Button>
        </Stack>
      </TopToolbar>
      <ThirdPartyClientFormDialog
        open={createOpen}
        mode="create"
        onClose={() => setCreateOpen(false)}
      />
    </>
  );
};

const EditClientButton: React.FC = () => {
  const record = useRecordContext<ThirdPartyClient>();
  const [open, setOpen] = React.useState(false);

  if (!record) return null;

  return (
    <>
      <Button size="small" onClick={() => setOpen(true)}>
        编辑
      </Button>
      <ThirdPartyClientFormDialog
        open={open}
        mode="edit"
        record={record}
        onClose={() => setOpen(false)}
      />
    </>
  );
};

const ThirdPartyClientListContent: React.FC = () => {
  const { total } = useListContext<ThirdPartyClient>();

  return (
    <Box sx={{ mt: 2 }}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          border: "1px solid rgba(148, 163, 184, 0.16)",
          background:
            "linear-gradient(180deg, rgba(15, 23, 42, 0.02) 0%, rgba(15, 23, 42, 0.04) 100%)",
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid rgba(148, 163, 184, 0.14)",
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              客户端信息列表
            </Typography>
            <Typography variant="body2" color="text.secondary">
              展示第三方客户端基础信息、时间信息与常用操作
            </Typography>
          </Box>
          <Typography
            variant="body2"
            sx={{
              px: 1.5,
              py: 0.75,
              borderRadius: 999,
              backgroundColor: "rgba(25, 118, 210, 0.08)",
              color: "primary.main",
              fontWeight: 600,
            }}
          >
            共 {total ?? 0} 条
          </Typography>
        </Box>

        <Datagrid
          bulkActionButtons={false}
          rowClick={false}
          sx={{
            "& .RaDatagrid-headerCell": {
              fontWeight: 700,
              backgroundColor: "rgba(15, 23, 42, 0.04)",
            },
            "& .RaDatagrid-row:hover": {
              backgroundColor: "rgba(25, 118, 210, 0.04)",
            },
          }}
        >
          <TextField source="id" label="ID" />
          <TextField source="cid" label="CID" />
          <TextField source="name" label="名称" />
          <FunctionField
            label="作用域(SScope)"
            render={(record: ThirdPartyClient) => <ScopeChips value={record.scope} />}
          />
          <TextField source="request_template_id" label="短信模板ID" />
          <DateField source="createdAt" label="创建时间" showTime />
          <DateField source="updatedAt" label="更新时间" showTime />
          <ClientDetailButton />
          <FunctionField label="更多" render={() => <ClientMoreActions />} />
        </Datagrid>
      </Paper>
    </Box>
  );
};

const ClientDetailDialog: React.FC<{
  open: boolean;
  clientId: string;
  onClose: () => void;
}> = ({ open, clientId, onClose }) => {
  const dataProvider = useDataProvider();
  const [record, setRecord] = React.useState<ThirdPartyClient | null>(null);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    dataProvider
      .getOne("thirdPartyClients", { id: clientId })
      .then((res: { data: ThirdPartyClient }) => {
        if (cancelled) return;
        setRecord(res.data);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId, dataProvider, open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>第三方客户端详情</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Typography variant="body2">加载中...</Typography>
        ) : record ? (
          <Stack spacing={1}>
            <Typography variant="body2">ID：{record.id}</Typography>
            <Typography variant="body2">CID：{record.cid}</Typography>
            <Typography variant="body2">名称：{record.name}</Typography>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.75 }}>
                Scope：
              </Typography>
              <ScopeChips value={record.scope} />
            </Box>
            <Typography variant="body2">
              Request Template ID：{record.request_template_id ?? "-"}
            </Typography>
            <Typography variant="body2">创建时间：{record.createdAt}</Typography>
            <Typography variant="body2">更新时间：{record.updatedAt}</Typography>
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>关闭</Button>
      </DialogActions>
    </Dialog>
  );
};

const ClientDetailButton: React.FC = () => {
  const record = useRecordContext<ThirdPartyClient>();
  const [open, setOpen] = React.useState(false);

  if (!record) return null;

  return (
    <>
      <Button size="small" onClick={() => setOpen(true)}>
        详情
      </Button>
      <ClientDetailDialog
        open={open}
        clientId={record.id}
        onClose={() => setOpen(false)}
      />
    </>
  );
};

const ClientMoreActions: React.FC = () => {
  const record = useRecordContext<ThirdPartyClient>();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [editOpen, setEditOpen] = React.useState(false);
  const [secret, setSecret] = React.useState<string | null>(null);
  const [confirmAction, setConfirmAction] = React.useState<"resetSecret" | "delete" | null>(
    null
  );
  const [actionLoading, setActionLoading] = React.useState(false);

  if (!record) return null;

  const closeMenu = () => setAnchorEl(null);

  const onEdit = () => {
    closeMenu();
    setEditOpen(true);
  };

  const openResetSecretConfirm = () => {
    closeMenu();
    setConfirmAction("resetSecret");
  };

  const openDeleteConfirm = () => {
    closeMenu();
    setConfirmAction("delete");
  };

  const closeConfirm = () => {
    if (actionLoading) return;
    setConfirmAction(null);
  };

  const confirm = async () => {
    if (!confirmAction) return;
    if (actionLoading) return;
    setActionLoading(true);
    try {
      if (confirmAction === "resetSecret") {
        const res: { data: ResetSecretResponse } =
          await dataProvider.resetThirdPartyClientSecret(record.id);
        if (res.data.secret) {
          setSecret(res.data.secret);
          notify("密钥已重置", { type: "success" });
        } else {
          notify("密钥已重置，但接口未返回 secret", { type: "warning" });
        }
        refresh();
        setConfirmAction(null);
        return;
      }

      await dataProvider.delete("thirdPartyClients", {
        id: record.id,
        previousData: record,
      });
      notify("删除成功", { type: "success" });
      refresh();
      setConfirmAction(null);
    } catch {
      notify(confirmAction === "resetSecret" ? "重置失败" : "删除失败", { type: "error" });
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <>
      <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={openResetSecretConfirm}>重置密钥</MenuItem>
        <MenuItem onClick={onEdit}>编辑</MenuItem>
        <MenuItem onClick={openDeleteConfirm} sx={{ color: "error.main" }}>
          删除
        </MenuItem>
      </Menu>

      <Dialog
        open={Boolean(confirmAction)}
        onClose={closeConfirm}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{confirmAction === "delete" ? "确认删除" : "确认重置密钥"}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1}>
            <Typography variant="body2">即将操作的客户端信息：</Typography>
            <Typography variant="body2" color="text.primary">
              名称：{record.name ?? "-"}
            </Typography>
            <Typography variant="body2" color="text.primary">
              CID：{record.cid ?? "-"}
            </Typography>
            <Typography variant="body2" color="text.primary">
              ID：{record.id}
            </Typography>
            {confirmAction === "resetSecret" ? (
              <Typography variant="body2" color="text.secondary">
                重置后旧密钥将失效，请谨慎操作。
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                删除后该客户端将不可用，请确认无业务依赖。
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={actionLoading} onClick={closeConfirm}>
            取消
          </Button>
          <Button
            disabled={actionLoading}
            variant="contained"
            color={confirmAction === "delete" ? "error" : "primary"}
            onClick={confirm}
          >
            {actionLoading ? "处理中..." : confirmAction === "delete" ? "确认删除" : "确认重置"}
          </Button>
        </DialogActions>
      </Dialog>

      <ThirdPartyClientFormDialog
        open={editOpen}
        mode="edit"
        record={record}
        onClose={() => setEditOpen(false)}
      />

      {secret ? (
        <ResetSecretDialog
          open={Boolean(secret)}
          secret={secret}
          onClose={() => setSecret(null)}
        />
      ) : null}
    </>
  );
};

const ResetSecretDialog: React.FC<{
  open: boolean;
  secret: string;
  onClose: () => void;
}> = ({ open, secret, onClose }) => {
  const notify = useNotify();

  const copySecret = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      notify("已复制密钥", { type: "success" });
    } catch {
      notify("复制失败，请手动复制", { type: "warning" });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>重置客户端密钥</DialogTitle>
      <DialogContent dividers>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
            {secret}
          </Typography>
          <Button
            size="small"
            startIcon={<ContentCopyIcon fontSize="small" />}
            onClick={copySecret}
          >
            复制
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>关闭</Button>
      </DialogActions>
    </Dialog>
  );
};

export const ThirdPartyClientList: React.FC = (props: Record<string, unknown>) => (
  <List
    {...props}
    empty={false}
    actions={<ThirdPartyClientListActions />}
    sx={{
      "& .RaList-main": {
        mt: 1,
      },
      "& .RaList-content": {
        backgroundColor: "transparent",
        boxShadow: "none",
      },
    }}
  >
    <ThirdPartyClientListContent />
  </List>
);
